import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createCustomField,
  customFieldQuery,
  customFieldsListQuery,
  customFieldsShellQuery,
  removeCustomField,
  updateCustomField,
  type CustomField,
  type CustomFieldType,
} from '../../api/settingsEaseedeskCustomFields'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Custom fields.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_custom_fields/index.php` + `list.php` + `edit_form.php`, si gest cu
 * gest fata de `index-1.js`, `list-1.js` si `edit_form-1.js`.
 *
 * PREVIZUALIZAREA CAMPULUI e transcrierea lui `buildField()` din
 * `helpers/form_fields_helper.php`: acelasi markup, acelasi `name`
 * (`custom_field_<ID>`) si aceleasi clase, ca ce se vede aici sa fie ce se vede
 * pe formularul de tichet. Campurile din previzualizare nu se trimit nicaieri -
 * sunt un desen, ca si in portal.
 *
 * FELUL CAMPULUI NU SE SCHIMBA dupa ce a fost facut, nici aici, nici in CI3:
 * pe el se sprijina formularele si raspunsurile deja scrise pe tichete. De-aia
 * fereastra de editare are numai numele, eticheta, lamurirea si optiunile.
 */

/** `buildField()`: previzualizarea, dupa felul campului. */
export function FieldPreview({ field }: { field: CustomField }) {
  const name = `custom_field_${field.ID}`

  switch (field.field_type) {
    case 'textarea':
      return (
        <div className="form-group">
          <textarea name={name} className="form-control input-sm wysiwyg-editor" readOnly />
        </div>
      )
    case 'select':
    case 'multiple_select':
      return (
        <div className="form-group">
          <select
            data-live-search="true"
            data-actions-box="true"
            name={name}
            className="form-control input-sm custom-selectpicker show-tick"
            multiple={field.field_type === 'multiple_select'}
            value={field.field_type === 'multiple_select' ? [] : ''}
            onChange={() => {}}
          >
            {field.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )
    case 'checkbox':
      return (
        <div className="form-group">
          <div className="checkbox">
            <label>
              <input type="checkbox" name={name} className="custom-checkbox" readOnly checked={false} />
            </label>
          </div>
        </div>
      )
    case 'radio':
      return (
        <div className="form-group">
          {field.options.map((option) => (
            <div className="radio" key={option}>
              <label>
                <input className="custom-radio" type="radio" name={name} value={option} readOnly checked={false} /> {option}
              </label>
            </div>
          ))}
        </div>
      )
    case 'number_input':
      return (
        <div className="form-group">
          <input type="number" name={name} className="form-control input-sm" readOnly />
        </div>
      )
    case 'date_picker':
    case 'time_picker':
    case 'datetime_picker': {
      const icon =
        field.field_type === 'date_picker'
          ? 'fa fa-calendar ico'
          : field.field_type === 'time_picker'
            ? 'far fa-clock ico'
            : 'far fa-calendar-check ico'
      const klass =
        field.field_type === 'date_picker'
          ? 'custom-datepicker'
          : field.field_type === 'time_picker'
            ? 'custom-timepicker'
            : 'custom-datetimepicker'
      return (
        <div className="form-group">
          <div className="input-icon">
            <i className={icon} aria-hidden="true"></i>
            <input type="text" name={name} className={`form-control input-sm ${klass}`} readOnly />
          </div>
        </div>
      )
    }
    case 'duration_picker':
      return (
        <div className="form-group">
          <input type="text" name={name} className="form-control input-sm custom-durationpicker" readOnly />
        </div>
      )
    default:
      return (
        <div className="form-group">
          <input type="text" name={name} className="form-control input-sm" readOnly />
        </div>
      )
  }
}

/** Casutele de optiuni, cu + si - ca in machete. */
export function OptionRows({ options, onChange }: { options: string[]; onChange(next: string[]): void }) {
  const rows = options.length > 0 ? options : ['']
  return (
    <div className="form-group" id="field-options">
      <label className="control-label">Field options</label>
      {rows.map((option, i) => (
        <div className="field-option" key={i}>
          <div className="input-group">
            <input
              className="form-control input-sm"
              type="text"
              name="field-options[]"
              value={option}
              onChange={(e) => {
                const next = [...rows]
                next[i] = e.target.value
                onChange(next)
              }}
            />
            <span className="input-group-btn">
              <button
                className="btn btn-default btn-sm add-new-option"
                type="button"
                onClick={() => onChange([...rows, ''])}
              >
                <i className="fas fa-plus"></i>
              </button>
              <button
                className="btn btn-default btn-sm remove-new-option"
                type="button"
                onClick={() => onChange(rows.filter((_, j) => j !== i))}
              >
                <i className="fas fa-minus"></i>
              </button>
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

export function TicketCustomFieldsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(customFieldsShellQuery())
  const list = useQuery(customFieldsListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState(0)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_custom_fields'] })
  }

  const remove = useMutation({
    mutationFn: (field: number) => removeCustomField(field),
    onSuccess: () => {
      toast('Custom field removed.', 'Success')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="Custom fields" />
  if (!shell.data) return <Spinner />

  const fields = list.data?.fields ?? []

  return (
    <div>
      <h4>
        <strong>Custom</strong> fields
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>custom</strong> fields
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create custom field
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The custom field list" />
        ) : !list.data ? (
          <Spinner />
        ) : fields.length === 0 ? (
          <div className="well corner-flip flip-bg-white" data-color="inverse">
            {/* `od-note-title`, ca in Comment types: `body.od-console h4:not([class]) *`
                (od_console.css) scrie fontul de titlu peste tot ce e in `h4`,
                iconita inclusa - triunghiul din Font Awesome 5 iesea patrat gol,
                iar "NOTE" in serif de 22px. O clasa pe `h4` il scoate de sub regula. */}
            <h4 className="od-note-title">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> <strong>NOTE</strong>{' '}
            </h4>
            <p>
              There are no <strong>custom</strong> fields. Press{' '}
              <strong>
                <i className="fas fa-plus" aria-hidden="true"></i> Create custom field
              </strong>{' '}
              button to create a new field.
            </p>
            <div className="flip"></div>
          </div>
        ) : (
          fields.map((field) => (
            <section className="settings" key={field.ID} id={`custom_field_row_${field.ID}`}>
              <div className="row">
                <div className="col-md-2">
                  <h5 style={{ width: 300 }}>
                    <i
                      className="fas fa-info-circle color-blue font-14"
                      style={{ cursor: 'default' }}
                      data-toggle="tooltip"
                      data-placement="top"
                      title={field.field_info}
                    >
                      {' '}
                    </i>
                    {field.field_name}
                  </h5>
                  <label className="color">
                    <strong>{field.field_type}</strong>
                  </label>
                </div>
                <div className="col-md-4">
                  <FieldPreview field={field} />
                </div>
                <div className="col-md-4">
                  <span className="label label-info">{field.field_label}</span>
                </div>
                <div className="col-md-2">
                  <div className="btn-group pull-right">
                    <button
                      type="button"
                      data-field_id={field.ID}
                      className="btn btn-primary btn-xs edit-custom-field"
                      onClick={() => setEdited(field.ID)}
                    >
                      <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-xs delete-custom-field"
                      data-field_id={field.ID}
                      onClick={() => {
                        if (!window.confirm('Are you sure?\nYou will not be able to recover this custom field!')) return
                        remove.mutate(field.ID)
                      }}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i> Remove
                    </button>
                  </div>
                </div>
              </div>
              <hr className="dotted" />
            </section>
          ))
        )}
      </div>

      <NewCustomFieldModal open={creating} types={shell.data.types} onClose={() => setCreating(false)} onSaved={reload} />
      <EditCustomFieldModal field={edited} onClose={() => setEdited(0)} onSaved={reload} />
    </div>
  )
}

/** `newCustomFieldModal` din macheta: felul, numele, eticheta, lamurirea, optiunile. */
export function NewCustomFieldModal({
  open,
  types,
  onClose,
  onSaved,
}: {
  open: boolean
  types: CustomFieldType[]
  onClose(): void
  onSaved(): void
}) {
  const [type, setType] = useState(types[0]?.value ?? 'text_input')
  const [name, setName] = useState('')
  const [label, setLabel] = useState('')
  const [info, setInfo] = useState('')
  const [options, setOptions] = useState<string[]>([''])

  useEffect(() => {
    if (!open) return
    setType(types[0]?.value ?? 'text_input')
    setName('')
    setLabel('')
    setInfo('')
    setOptions([''])
  }, [open, types])

  const needsOptions = types.find((t) => t.value === type)?.options ?? false

  const create = useMutation({
    mutationFn: () => createCustomField(type, name.trim(), label.trim(), info.trim(), needsOptions ? options : []),
    onSuccess: () => {
      toast('New custom field created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="newCustomFieldModal"
      labelledBy="newCustomFieldModalLabel"
      open={open}
      onClose={onClose}
      title="New custom field"
    >
      <form
        id="new-custom-field-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          create.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="field_type" className="control-label">
            Field type
          </label>
          <select
            className="form-control input-sm"
            id="field_type"
            name="field_type"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {types.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="field-name" className="control-label">
            Field name
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="field-name"
            name="field_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="field-label" className="control-label">
            Field label
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="field-label"
            name="field_label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="field-info" className="control-label">
            Field info
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="field-info"
            name="field_info"
            value={info}
            onChange={(e) => setInfo(e.target.value)}
          />
        </div>
        {needsOptions && <OptionRows options={options} onChange={setOptions} />}
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary submit-new-field" disabled={name.trim() === '' || create.isPending}>
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * `editCustomFieldModal`: continutul lui venea de la server
 * (`edit_custom_field_form`), si tot de acolo vine si aici - fara felul
 * campului, care nu se schimba.
 */
export function EditCustomFieldModal({
  field,
  onClose,
  onSaved,
}: {
  field: number
  onClose(): void
  onSaved(): void
}) {
  const query = useQuery(customFieldQuery(field))
  const [name, setName] = useState('')
  const [label, setLabel] = useState('')
  const [info, setInfo] = useState('')
  const [options, setOptions] = useState<string[]>([''])

  useEffect(() => {
    if (!query.data || query.data.ID !== field) return
    setName(query.data.field_name)
    setLabel(query.data.field_label)
    setInfo(query.data.field_info)
    setOptions(query.data.options.length > 0 ? query.data.options : [''])
  }, [query.data, field])

  const save = useMutation({
    mutationFn: () =>
      updateCustomField(field, name.trim(), label.trim(), info.trim(), query.data?.has_options ? options : []),
    onSuccess: () => {
      toast('Settings saved.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="editCustomFieldModal"
      labelledBy="editCustomFieldModalLabel"
      open={field > 0}
      onClose={onClose}
      title="Edit custom field"
    >
      {!query.data ? (
        <Spinner />
      ) : (
        <form
          id="edit-custom-field-form"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim() === '') return
            save.mutate()
          }}
        >
          <div className="form-group">
            <label htmlFor="edit-field-name" className="control-label">
              Field name
            </label>
            <input type="hidden" name="custom_field_id" value={field} readOnly />
            <input
              type="text"
              className="form-control input-sm"
              id="edit-field-name"
              name="field_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-field-label" className="control-label">
              Field label
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="edit-field-label"
              name="field_label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-field-info" className="control-label">
              Field info
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="edit-field-info"
              name="field_info"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
            />
          </div>
          {query.data.has_options && <OptionRows options={options} onChange={setOptions} />}
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button
              type="submit"
              className="btn btn-primary submit-update-field"
              disabled={name.trim() === '' || save.isPending}
            >
              Update
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
